import posthog from "posthog-js";
import type { IUser } from "@/domains/shared/models/IUser";
import { buildIdentifyProperties } from "./buildIdentifyProperties";
import {
  APP_FRONTEND_EVENT_NAME_SET,
  FORBIDDEN_CLINICAL_PROP_KEYS,
  type AppFrontendEventName,
} from "./frontendEventNames";

export type ProductAnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

function stripForbiddenClinicalProps(
  properties: ProductAnalyticsProperties | undefined,
): ProductAnalyticsProperties | undefined {
  if (!properties) {
    return undefined;
  }

  const cleaned: ProductAnalyticsProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if ((FORBIDDEN_CLINICAL_PROP_KEYS as readonly string[]).includes(key)) {
      continue;
    }
    cleaned[key] = value;
  }
  return cleaned;
}

/**
 * Catalog-only FE capture. Unknown names and clinical resource ids are rejected (BR-04, BR-14).
 * Does not dual-emit BE truths (BR-05).
 */
export function captureProductEvent(
  eventName: string,
  properties?: ProductAnalyticsProperties,
): boolean {
  if (!APP_FRONTEND_EVENT_NAME_SET.has(eventName)) {
    if (import.meta.env.DEV) {
      console.warn(`[productAnalytics] rejected unknown FE event: ${eventName}`);
    }
    return false;
  }

  if (properties) {
    for (const key of FORBIDDEN_CLINICAL_PROP_KEYS) {
      if (Object.prototype.hasOwnProperty.call(properties, key) && properties[key] != null) {
        if (import.meta.env.DEV) {
          console.warn(`[productAnalytics] rejected forbidden prop ${key} on ${eventName}`);
        }
        return false;
      }
    }
  }

  const safeProps = stripForbiddenClinicalProps(properties);
  posthog.capture(eventName as AppFrontendEventName, safeProps);
  return true;
}

/** Identify after auth session establish (login / register / me). */
export function identifyProductUser(user: IUser): void {
  if (!user?.id?.trim()) {
    return;
  }

  const props = buildIdentifyProperties(user);
  posthog.identify(user.id, props);
}

/** Reset on logout / session clear so distinct_id does not leak (AC-04, E-04). */
export function resetProductAnalytics(): void {
  posthog.reset();
}
